import { useEffect, useState } from "react";
import { useAuth } from "../../lib/auth";
import { Save, RefreshCw, Info } from "lucide-react";

interface Template {
  id?: number;
  sucursalId: number | null;
  tipo: string;
  asunto: string;
  cuerpo: string;
  activo: boolean;
}

const TIPOS: { key: string; label: string; icon: string; desc: string; grupo?: string }[] = [
  // Reservas
  { key: "reserva_recibida", label: "Reserva recibida",       icon: "📋", desc: "Se envía cuando el cliente hace una reserva", grupo: "Reservas" },
  { key: "confirmada",       label: "Reserva confirmada",     icon: "✅", desc: "Cuando el staff confirma la reserva (incluye QR)", grupo: "Reservas" },
  { key: "por_expirar",      label: "Recordatorio de expiración", icon: "⏰", desc: "1 hora antes de que se cancele automáticamente", grupo: "Reservas" },
  { key: "cancelada",        label: "Reserva cancelada",      icon: "❌", desc: "Cuando la reserva es cancelada", grupo: "Reservas" },
  { key: "bloqueo",          label: "Pista/zona no disponible", icon: "⚠️", desc: "Cuando un bloqueo afecta una reserva existente", grupo: "Reservas" },
  { key: "lista_espera_recibida",  label: "Lista de espera — recibida", icon: "⌛", desc: "Cuando la reserva entra en lista de espera por cap llena", grupo: "Reservas" },
  { key: "lista_espera_promovida", label: "Lista de espera — promovida", icon: "🎉", desc: "Cuando se libera un cupo y la reserva es promovida a pendiente", grupo: "Reservas" },
  { key: "cumpleanos",       label: "Feliz cumpleaños",       icon: "🎂", desc: "Email especial de cumpleaños con descuento", grupo: "Reservas" },
  { key: "promocion",        label: "Promoción / Descuento",  icon: "🎁", desc: "Envío manual de ofertas y comunicados", grupo: "Reservas" },
  // Campañas masivas (usadas por el modal de Cliente 360)
  { key: "campana_vip_email",         label: "Campaña VIP — Email",       icon: "👑", desc: "Email a clientes de alto ticket y recurrentes", grupo: "Campañas" },
  { key: "campana_vip_whatsapp",      label: "Campaña VIP — WhatsApp",    icon: "👑", desc: "WhatsApp a clientes de alto ticket y recurrentes", grupo: "Campañas" },
  { key: "campana_riesgo_email",      label: "Re-engagement — Email",     icon: "⚠️", desc: "Email a clientes ausentes pero valiosos", grupo: "Campañas" },
  { key: "campana_riesgo_whatsapp",   label: "Re-engagement — WhatsApp",  icon: "⚠️", desc: "WhatsApp a clientes ausentes pero valiosos", grupo: "Campañas" },
  { key: "campana_cumpleanos_email",  label: "Cumpleaños — Email",        icon: "🎂", desc: "Email de saludo de cumpleaños masivo", grupo: "Campañas" },
  { key: "campana_cumpleanos_whatsapp", label: "Cumpleaños — WhatsApp",   icon: "🎂", desc: "WhatsApp de saludo de cumpleaños masivo", grupo: "Campañas" },
  { key: "campana_general_email",     label: "Mensaje genérico — Email",  icon: "✉️", desc: "Plantilla libre para campañas custom", grupo: "Campañas" },
  { key: "campana_general_whatsapp",  label: "Mensaje genérico — WhatsApp", icon: "💬", desc: "Plantilla libre para campañas custom por WhatsApp", grupo: "Campañas" },
];

const VARIABLES: { var: string; desc: string }[] = [
  { var: "{{nombreCliente}}",  desc: "Nombre del cliente" },
  { var: "{{idReserva}}",      desc: "Número de reserva" },
  { var: "{{fechaReserva}}",   desc: "Fecha de la reserva" },
  { var: "{{horaReserva}}",    desc: "Hora de la reserva" },
  { var: "{{numPersonas}}",    desc: "Cantidad de personas" },
  { var: "{{sucursalNombre}}", desc: "Nombre del restaurante" },
  { var: "{{telefonoSucursal}}", desc: "Teléfono de contacto" },
  { var: "{{whatsappLink}}",   desc: "Enlace de WhatsApp" },
  { var: "{{linkConfirmar}}",  desc: "Enlace para autoconfirmar" },
  { var: "{{linkCheckin}}",    desc: "Enlace del QR de check-in" },
  { var: "{{linkReservar}}",   desc: "Enlace para nueva reserva" },
  { var: "{{motivoBloqueo}}",  desc: "Motivo del bloqueo (template bloqueo)" },
  { var: "{{descuento}}",      desc: "Descuento (template cumpleaños)" },
  { var: "{{cuerpoPromocion}}","desc": "Texto de la promoción" },
  // Para campañas masivas (segmentos VIP / En riesgo / Cumpleaños)
  { var: "{{nombre}}",          desc: "Nombre completo (campañas)" },
  { var: "{{primer_nombre}}",   desc: "Primer nombre (campañas)" },
  { var: "{{tratamiento}}",     desc: "Sr./Sra. según sexo (campañas)" },
  { var: "{{visitas}}",         desc: "Total de visitas del cliente" },
  { var: "{{ticket_promedio}}", desc: "Ticket promedio USD" },
  { var: "{{gasto_total}}",     desc: "Gasto total USD" },
  { var: "{{ultima_visita}}",   desc: "Fecha de la última visita" },
  { var: "{{dias_sin_volver}}", desc: "Días sin volver" },
  // Para lista de espera
  { var: "{{esperaPosicion}}",  desc: "Posición en la lista de espera" },
  { var: "{{sucursalNombre}}",  desc: "Nombre de la sucursal (espera)" },
];

const SUCURSALES_LOCAL = [
  { id: 1, nombre: "Francelina" },
  { id: 2, nombre: "Attimo" },
  { id: 3, nombre: "Mam" },
  { id: 4, nombre: "Portarossa" },
];

export function EmailTemplatesPage() {
  const { token } = useAuth();
  const [sucursalId, setSucursalId] = useState<number | null>(null); // null = global
  const [tipoSel, setTipoSel] = useState("reserva_recibida");
  const [templates, setTemplates] = useState<Record<string, Template>>({});
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [sucursales, setSucursales] = useState(SUCURSALES_LOCAL);

  const headers = { Authorization: `Bearer ${token}`, "Content-Type": "application/json" };

  // Cargar sucursales
  useEffect(() => {
    fetch("/api/v1/sucursales").then(r => r.json())
      .then(d => Array.isArray(d) && setSucursales(d.map((s: any) => ({ id: s.id, nombre: s.nombre }))))
      .catch(() => {});
  }, []);

  // Cargar templates
  useEffect(() => {
    if (!token) return;
    fetch(`/api/v1/email-templates?sucursalId=${sucursalId ?? ""}`, { headers })
      .then(r => r.json())
      .then((data: Template[]) => {
        const map: Record<string, Template> = {};
        TIPOS.forEach(t => {
          const found = data.find(d => d.tipo === t.key);
          map[t.key] = found ?? {
            sucursalId,
            tipo: t.key,
            asunto: "",
            cuerpo: "",
            activo: true,
          };
        });
        setTemplates(map);
      })
      .catch(() => {});
  }, [sucursalId, token]);

  const current = templates[tipoSel];

  const setField = (k: "asunto" | "cuerpo" | "activo", v: string | boolean) => {
    setTemplates(ts => ({
      ...ts,
      [tipoSel]: { ...ts[tipoSel], [k]: v },
    }));
  };

  const guardar = async () => {
    if (!current) return;
    setSaving(true);
    try {
      const url = current.id
        ? `/api/v1/email-templates/${current.id}`
        : `/api/v1/email-templates`;
      await fetch(url, {
        method: current.id ? "PUT" : "POST",
        headers,
        body: JSON.stringify({ ...current, sucursalId }),
      });
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } finally {
      setSaving(false);
    }
  };

  const restaurarDefault = async () => {
    if (!confirm("¿Restaurar la plantilla por defecto del sistema?")) return;
    await fetch(`/api/v1/email-templates/default/${tipoSel}`, { headers })
      .then(r => r.json())
      .then(d => {
        setTemplates(ts => ({ ...ts, [tipoSel]: { ...d, sucursalId } }));
      })
      .catch(() => {});
  };

  const insertarVar = (v: string) => {
    setField("cuerpo", (current?.cuerpo ?? "") + v);
  };

  const tipo = TIPOS.find(t => t.key === tipoSel);

  return (
    <div className="mx-auto max-w-4xl px-6 py-10 space-y-6">
      <div>
        <h1 className="font-display text-2xl font-bold text-ink-800">Plantillas de Email</h1>
        <p className="text-ink-500 text-sm mt-1">
          Personaliza los mensajes automáticos que reciben tus clientes. Usa variables entre {"{{"}dobles llaves{"}}"} para insertar datos dinámicos.
        </p>
      </div>

      {/* Alcance */}
      <div className="card p-4">
        <label className="block text-xs font-semibold text-ink-600 mb-2 uppercase tracking-wide">
          Aplicar plantilla a:
        </label>
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => setSucursalId(null)}
            className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${sucursalId === null ? "bg-brand-600 text-white border-brand-600" : "border-ink-300 text-ink-600 hover:border-ink-500"}`}
          >
            🌐 Todas las sucursales
          </button>
          {sucursales.map(s => (
            <button key={s.id}
              onClick={() => setSucursalId(s.id)}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium border transition-colors ${sucursalId === s.id ? "bg-brand-600 text-white border-brand-600" : "border-ink-300 text-ink-600 hover:border-ink-500"}`}
            >
              {s.nombre}
            </button>
          ))}
        </div>
        <p className="text-xs text-ink-400 mt-2">
          {sucursalId === null
            ? "Esta plantilla aplica a todas las sucursales que no tengan una plantilla específica."
            : `Esta plantilla sobreescribe la global solo para ${sucursales.find(s => s.id === sucursalId)?.nombre}.`}
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Lista de tipos agrupados */}
        <div className="space-y-3">
          {["Reservas", "Campañas"].map((grupoNombre) => (
            <div key={grupoNombre}>
              <div className="text-[10px] uppercase tracking-widest text-ink-400 font-bold px-2 mb-1">
                {grupoNombre}
              </div>
              <div className="space-y-1">
                {TIPOS.filter(t => (t.grupo ?? "Reservas") === grupoNombre).map(t => (
                  <button key={t.key}
                    onClick={() => setTipoSel(t.key)}
                    className={`w-full text-left px-3 py-2.5 rounded-lg text-sm transition-colors flex items-center gap-2.5 ${tipoSel === t.key ? "bg-brand-600 text-white" : "hover:bg-ink-100 text-ink-700"}`}>
                    <span className="text-base">{t.icon}</span>
                    <span className="font-medium leading-tight">{t.label}</span>
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>

        {/* Editor */}
        <div className="lg:col-span-2 space-y-4">
          {tipo && current && (
            <>
              <div className="flex items-start justify-between gap-3">
                <div>
                  <h3 className="font-semibold text-ink-800">{tipo.icon} {tipo.label}</h3>
                  <p className="text-xs text-ink-400 mt-0.5">{tipo.desc}</p>
                </div>
                <label className="flex items-center gap-2 shrink-0 cursor-pointer">
                  <div onClick={() => setField("activo", !current.activo)}
                    className={`w-9 h-5 rounded-full transition-colors relative ${current.activo ? "bg-brand-600" : "bg-ink-300"}`}>
                    <div className={`absolute top-0.5 left-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${current.activo ? "translate-x-4" : "translate-x-0"}`} />
                  </div>
                  <span className="text-xs text-ink-600">Activa</span>
                </label>
              </div>

              {/* Asunto */}
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1">Asunto del email</label>
                <input
                  value={current.asunto}
                  onChange={e => setField("asunto", e.target.value)}
                  className="input w-full text-sm"
                  placeholder="Asunto del email..."
                />
              </div>

              {/* Cuerpo */}
              <div>
                <label className="block text-xs font-semibold text-ink-600 mb-1">Cuerpo del mensaje</label>
                <textarea
                  value={current.cuerpo}
                  onChange={e => setField("cuerpo", e.target.value)}
                  rows={10}
                  className="input w-full resize-y text-sm font-mono"
                  placeholder="Escribe el mensaje aquí. Usa {{variable}} para datos dinámicos..."
                />
              </div>

              {/* Variables disponibles */}
              <div className="card p-3 bg-ink-50">
                <div className="flex items-center gap-1.5 mb-2 text-xs font-semibold text-ink-600">
                  <Info className="size-3.5" /> Variables disponibles (click para insertar)
                </div>
                <div className="flex flex-wrap gap-1.5">
                  {VARIABLES.map(v => (
                    <button key={v.var}
                      onClick={() => insertarVar(v.var)}
                      title={v.desc}
                      className="text-xs px-2 py-0.5 bg-white border border-ink-200 rounded text-brand-600 hover:bg-brand-50 hover:border-brand-300 font-mono transition-colors">
                      {v.var}
                    </button>
                  ))}
                </div>
              </div>

              {/* Acciones */}
              <div className="flex gap-3">
                <button onClick={guardar} disabled={saving}
                  className="btn-primary flex items-center gap-2 flex-1 justify-center">
                  <Save className="size-4" />
                  {saving ? "Guardando..." : saved ? "✓ Guardado" : "Guardar plantilla"}
                </button>
                <button onClick={restaurarDefault}
                  className="btn-secondary flex items-center gap-2 text-sm" title="Restaurar la plantilla por defecto">
                  <RefreshCw className="size-4" />
                  Restaurar default
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
